import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Spinner } from '@/components/Spinner'
import { match } from '@/utils/pattern-match-util'
import type { SubmissionStatus } from '@/features/jobs/drafting/SubmissionStatusModalViewModel'
import styles from './SubmissionStatusModal.module.css'
import { byDesign } from '@/infra/design'
import imgStripeConnectingUrl from '@/assets/misc/img_stripe-connecting.svg'
import StripeConnectingIllustration from '@/components/SpotIllustration/StripeConnectingIllustration'
import imgStripeErrorUrl from '@/assets/misc/img_stripe-error.svg'
import StripeErrorIllustration from '@/components/SpotIllustration/StripeErrorIllustration'
import imgStripeSuccessUrl from '@/assets/misc/img_stripe-success.svg'
import StripeSuccessIllustration from '@/components/SpotIllustration/StripeSuccessIllustration'

export interface SubmissionStatusModalProps {
  showing: boolean
  status: SubmissionStatus
  onClose: () => unknown
  error: string
  jobDisplayName: string
  /**
   * The rendered payment method that was charged, if any.
   */
  paymentMethod?: ReactNode
  onCreateAnother: () => unknown
  continueTo?: string
  isIllustrationShown: boolean
}

export function SubmissionStatusModal({
  showing,
  status,
  onClose,
  error,
  jobDisplayName,
  paymentMethod,
  onCreateAnother,
  continueTo,
  isIllustrationShown,
}: SubmissionStatusModalProps) {
  // We can only close the modal if the submission failed.
  // Otherwise the user needs to take an action.
  const canClose = status === 'Failed'

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onClose}
      disableCloseOnEsc={!canClose}
      disableCloseOnOutsideClick={!canClose}
    >
      <ModalSheetHeader withCloseButton={canClose} />
      {match(status, {
        ['Submitting']: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock testId="draft-editor-submission-status-submitting">
                <Illustration
                  status={'Submitting'}
                  isShown={isIllustrationShown}
                />
                <ModalSheetContentTitle>
                  Please wait, your request is being processed.
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  This may take a few moments.
                </ModalSheetContentDescription>
                <div className={styles.spinner}>
                  <Spinner size={36} />
                </div>
              </ModalSheetContentBlock>
            </ModalSheetContent>
          </>
        ),
        ['Failed']: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <Illustration status={'Failed'} isShown={isIllustrationShown} />
                <ModalSheetContentTitle>
                  We were unable to process your request.
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {error}
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button label="Close" onPress={onClose} />
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        ['Success']: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <Illustration
                  status={'Success'}
                  isShown={isIllustrationShown}
                />
                <ModalSheetContentTitle>
                  Your request was submitted!
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  Your <strong>{jobDisplayName}</strong> request has been
                  successfully submitted.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
              {paymentMethod && (
                <div className={styles.paymentMethod}>{paymentMethod}</div>
              )}
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterButtonGroup>
                <Button label="Create Another" onPress={onCreateAnother} />
              </ModalSheetFooterButtonGroup>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button
                  label="Continue"
                  variant="primary"
                  to={continueTo}
                  replace
                  testId="draft-editor-submission-status-continue-button"
                />
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
}

function Illustration({
  status,
  isShown,
}: {
  status: SubmissionStatus
  isShown: boolean
}) {
  return (
    <>
      {isShown && (
        <ModalSheetContentIllustration
          src={match(status, {
            ['Submitting']: () => imgStripeConnectingUrl,
            ['Failed']: () => imgStripeErrorUrl,
            ['Success']: () => imgStripeSuccessUrl,
          })}
          illustration={byDesign(
            undefined,
            match(status, {
              ['Submitting']: () => StripeConnectingIllustration,
              ['Failed']: () => StripeErrorIllustration,
              ['Success']: () => StripeSuccessIllustration,
            }),
          )}
        />
      )}
    </>
  )
}
